"use client";

import Link from "next/link";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Circle, HeartPulse, Plus } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { AftercareCheckins } from "@/components/AftercareCheckins";
import {
  CareCategory,
  CareItem,
  createCareItem,
  getCareItems,
  setCareItemCompleted,
} from "@/features/care/api";
import { getOrders, OrderRecord } from "@/features/orders/api";

const categoryLabels: Record<CareCategory, string> = {
  VACCINATION: "예방접종",
  HEALTH: "건강",
  TRAINING: "훈련",
};

export default function CarePage() {
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [orderId, setOrderId] = useState("");
  const [items, setItems] = useState<CareItem[]>([]);
  const [category, setCategory] = useState<CareCategory>("HEALTH");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const completedOrders = useMemo(
    () => orders.filter((order) => order.status === "COMPLETED"),
    [orders],
  );

  useEffect(() => {
    getOrders()
      .then((result) => {
        setOrders(result);
        setOrderId(
          result.find((order) => order.status === "COMPLETED")?.id ?? "",
        );
      })
      .catch(() => setError("완료된 거래를 불러오지 못했습니다."))
      .finally(() => setLoading(false));
  }, []);

  const loadItems = useCallback(async () => {
    if (!orderId) {
      setItems([]);
      return;
    }
    setError("");
    try {
      setItems(await getCareItems(orderId));
    } catch {
      setError("돌봄 항목을 불러오지 못했습니다.");
    }
  }, [orderId]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!orderId || !title.trim()) return;
    setSaving(true);
    setError("");
    try {
      await createCareItem(orderId, {
        category,
        title: title.trim(),
        note: note.trim() || undefined,
        dueDate: dueDate || undefined,
      });
      setTitle("");
      setNote("");
      setDueDate("");
      await loadItems();
    } catch {
      setError("돌봄 항목을 저장하지 못했습니다.");
    } finally {
      setSaving(false);
    }
  }

  async function toggle(item: CareItem) {
    setError("");
    try {
      const updated = await setCareItemCompleted(
        orderId,
        item.id,
        !item.completedAt,
      );
      setItems((current) =>
        current.map((candidate) =>
          candidate.id === item.id ? updated : candidate,
        ),
      );
    } catch {
      setError("완료 상태를 변경하지 못했습니다.");
    }
  }

  return (
    <main className="care-page">
      <SiteHeader compact />
      <section className="care-hero">
        <div className="shell">
          <span className="eyebrow">AFTERCARE</span>
          <h1>우리 강아지 돌봄 checklist</h1>
          <p>
            인도 완료 후 예방접종·건강·훈련 일정을 한곳에 직접 기록하고
            확인하세요.
          </p>
        </div>
      </section>

      <section className="care-body shell">
        <aside className="care-notice">
          <HeartPulse aria-hidden="true" />
          <div>
            <strong>일정은 자동 처방하지 않습니다.</strong>
            <p>
              예방접종과 진료 일정은 수의사, 훈련 계획은 전문가와 확인한 뒤
              기록하세요. 이 checklist는 진단이나 의료 조언을 대신하지 않습니다.
            </p>
          </div>
        </aside>

        {loading ? (
          <div className="care-empty">거래 내역을 불러오고 있습니다.</div>
        ) : completedOrders.length === 0 ? (
          <div className="care-empty">
            <h2>아직 인도 완료된 거래가 없습니다</h2>
            <p>
              구매자가 인도를 확인해 거래가 완료되면 돌봄 checklist를 사용할 수
              있습니다.
            </p>
            <Link href="/orders">주문 내역 보기</Link>
          </div>
        ) : (
          <>
            <label className="care-order-select">
              관리할 강아지
              <select
                value={orderId}
                onChange={(event) => setOrderId(event.target.value)}
              >
                {completedOrders.map((order) => (
                  <option key={order.id} value={order.id}>
                    강아지 #{order.dogId} · 주문 {order.id.slice(0, 8)}
                  </option>
                ))}
              </select>
            </label>

            <form className="care-form" onSubmit={submit}>
              <h2>돌봄 항목 추가</h2>
              <div className="care-form-grid">
                <label>
                  분류
                  <select
                    value={category}
                    onChange={(event) =>
                      setCategory(event.target.value as CareCategory)
                    }
                  >
                    {Object.entries(categoryLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  항목
                  <input
                    required
                    maxLength={160}
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    placeholder="예: 동물병원에서 2차 접종 일정 확인"
                  />
                </label>
                <label>
                  예정일 (선택)
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(event) => setDueDate(event.target.value)}
                  />
                </label>
                <label className="care-note">
                  메모 (선택)
                  <textarea
                    maxLength={500}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                  />
                </label>
              </div>
              <button disabled={saving}>
                <Plus aria-hidden="true" />
                {saving ? "저장 중" : "항목 추가"}
              </button>
            </form>

            {error && (
              <p className="care-error" role="alert">
                {error}
              </p>
            )}
            <div className="care-list">
              {items.length === 0 ? (
                <p className="care-empty">
                  직접 확인한 첫 돌봄 일정을 추가해 보세요.
                </p>
              ) : (
                items.map((item) => (
                  <article
                    key={item.id}
                    className={item.completedAt ? "is-complete" : ""}
                  >
                    <button
                      type="button"
                      onClick={() => void toggle(item)}
                      aria-label={`${item.title} ${item.completedAt ? "미완료로 변경" : "완료로 변경"}`}
                    >
                      {item.completedAt ? <CheckCircle2 /> : <Circle />}
                    </button>
                    <div>
                      <span>{categoryLabels[item.category]}</span>
                      <h3>{item.title}</h3>
                      {item.dueDate && (
                        <time dateTime={item.dueDate}>예정 {item.dueDate}</time>
                      )}
                      {item.note && <p>{item.note}</p>}
                    </div>
                  </article>
                ))
              )}
            </div>
            <AftercareCheckins orderId={orderId} />
          </>
        )}
        {error && completedOrders.length === 0 && (
          <p className="care-error" role="alert">
            {error}
          </p>
        )}
      </section>
    </main>
  );
}
